import { AsyncPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ScrollTrackerDirective } from '@modules/shared/directives/scroll-tracker.directive';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { Encounter, EncounterStatus } from '@pixacare/domain';
import { PaginationQuery } from '@pixacare/shared';
import { TuiButton, TuiDialogContext, TuiNotificationService, TuiRadio } from '@taiga-ui/core';
import { TuiBadge, TuiBlock, TuiChip } from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { catchError, map, Observable, of } from 'rxjs';
import { PatientHttpService } from 'src/app/services/http/patient.http.service';
import { VirtualScrollService } from 'src/app/services/virtual-scroll.service';
import {
  encounterStatusConfig,
  unknownEncouterStatusConfig,
} from 'src/app/shared/models/encounters/encounter-status.config';
import { QueryReportConfig } from 'src/app/shared/models/report-config';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';

@Component({
  templateUrl: './patient-encounter-picker.component.html',
  providers: [VirtualScrollService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    EmptyStateComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslocoDirective,
    AsyncPipe,
    ScrollTrackerDirective,
    TuiBlock,
    TuiRadio,
    TuiChip,
    DatePipe,
    TuiBadge,
    LoaderComponent,
    TuiButton,
    NgTemplateOutlet,
  ],
})
export class PatientEncounterPickerComponent {
  private patientService = inject(PatientHttpService);
  private translocoService = inject(TranslocoService);
  private readonly alertService = inject<TuiNotificationService>(TuiNotificationService);
  private virtualScrollService = inject<VirtualScrollService<Encounter>>(VirtualScrollService);
  private readonly context = inject<
    TuiDialogContext<
      string,
      {
        patientId: number;
        clientCode: string;
        patientReportConfig?: QueryReportConfig;
      }
    >
  >(POLYMORPHEUS_CONTEXT);

  readonly patientId: number = this.context.data.patientId;
  readonly clientCode: string = this.context.data.clientCode;
  readonly patientReportConfig: QueryReportConfig = this.context.data.patientReportConfig ?? {
    query: new PaginationQuery({ orderBy: ['created_on|desc'] }),
  };

  readonly encounters$: Observable<Encounter[]> = this.virtualScrollService.init(
    this.patientService.getPatientEncounters(this.clientCode, this.patientId).pipe(
      map((encounters) =>
        encounters.sort(
          (firstEncounter, secondEncounter) => secondEncounter.fromDate.getTime() - firstEncounter.fromDate.getTime(),
        ),
      ),
      catchError(() => {
        this.alertService
          .open(this.translocoService.translate('Veuillez réessayer plus tard'), {
            label: this.translocoService.translate('Impossible de récupérer les séjours du patient'),
            appearance: 'negative',
          })
          .subscribe();
        this.close();
        return of([]);
      }),
    ),
  );

  readonly EncounterStatus = EncounterStatus;
  readonly encounterStatusConfig = encounterStatusConfig;
  readonly unknownEncouterStatusConfig = unknownEncouterStatusConfig;

  readonly formGroup = new UntypedFormGroup({
    encounterId: new UntypedFormControl(null, [Validators.required]),
  });

  loadNextPage(): void {
    this.virtualScrollService.loadNextPage();
  }

  close(): void {
    this.context.completeWith(null);
  }

  confirm(): void {
    const value = this.formGroup.get('encounterId').value;

    if (!value) {
      return;
    }

    this.context.completeWith(value);
  }
}
